"use strict";

(() => {
  const revealElements = document.querySelectorAll(".omega-reveal");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  if (!revealElements.length || !("IntersectionObserver" in window)) {
    return;
  }

  if (reducedMotionQuery.matches) {
    revealElements.forEach((element) => element.classList.add("is-visible"));
    return;
  }

  document.documentElement.classList.add("has-scroll-reveals");

  const revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.classList.add("is-visible");
      observer.unobserve(entry.target);
    });
  }, {
    threshold: 0.12,
    rootMargin: "0px 0px -8% 0px",
  });

  revealElements.forEach((element) => revealObserver.observe(element));
})();

(() => {
  const hero = document.querySelector(".omega-hero");
  const bike = hero?.querySelector(".omega-hero__bike");
  const desktopQuery = window.matchMedia("(min-width: 48.0625rem)");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  if (!hero || !bike) {
    return;
  }

  let animationFrame;

  const updateBikeParallax = () => {
    animationFrame = undefined;

    if (!desktopQuery.matches || reducedMotionQuery.matches) {
      bike.style.transform = "";
      return;
    }

    const heroBounds = hero.getBoundingClientRect();
    const heroBottom = heroBounds.bottom + window.scrollY;
    // Counteract page scroll to hold the bike in its initial viewport position.
    // Keeping it inside the hero preserves clipping as the next section enters.
    const offset = Math.min(Math.max(window.scrollY, 0), heroBottom);

    bike.style.transform = `translate3d(0, ${offset}px, 0)`;
  };

  const requestBikeParallaxUpdate = () => {
    if (!animationFrame) {
      animationFrame = window.requestAnimationFrame(updateBikeParallax);
    }
  };

  window.addEventListener("scroll", requestBikeParallaxUpdate, { passive: true });
  window.addEventListener("resize", requestBikeParallaxUpdate);
  desktopQuery.addEventListener("change", requestBikeParallaxUpdate);
  reducedMotionQuery.addEventListener("change", requestBikeParallaxUpdate);
  requestBikeParallaxUpdate();
})();

(() => {
  const bar = document.querySelector(".omega-mobile-sticky-cta");
  const sections = ["#omega-configurator", "#final-cta"]
    .map((selector) => document.querySelector(selector))
    .filter(Boolean);

  if (!bar || !sections.length || !("IntersectionObserver" in window)) return;

  const visibleSections = new Set();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) visibleSections.add(entry.target);
      else visibleSections.delete(entry.target);
    });
    bar.classList.toggle("is-suppressed", visibleSections.size > 0);
  }, { rootMargin: "-68px 0px -56px 0px" });

  sections.forEach((section) => observer.observe(section));
})();

(() => {
  const wall = document.querySelector("[data-review-wall]");
  const grid = wall?.querySelector(".omega-reviews__grid");
  const cards = grid ? [...grid.children] : [];
  const toggle = document.querySelector("[data-review-toggle]");

  if (!wall || !grid || !cards.length || !toggle) return;

  const getCollapsedHeight = () => {
    const wallTop = wall.getBoundingClientRect().top;
    const firstRowTop = cards[0].getBoundingClientRect().top;
    const firstRowCount = cards.filter((card) =>
      Math.abs(card.getBoundingClientRect().top - firstRowTop) < 1
    ).length;
    const targetIndex = firstRowCount === 1 ? 2 : firstRowCount;
    const targetCard = cards[targetIndex];

    if (!targetCard) return grid.scrollHeight;

    const targetTop = targetCard.getBoundingClientRect().top;
    const targetRowCards = cards.filter((card) =>
      Math.abs(card.getBoundingClientRect().top - targetTop) < 1
    );
    const targetRowHeight = Math.max(...targetRowCards.map((card) => card.getBoundingClientRect().height));
    return Math.ceil(targetTop - wallTop + targetRowHeight * 0.52);
  };

  const setCollapsedHeight = () => {
    if (!wall.classList.contains("is-collapsed")) return;
    wall.style.maxHeight = `${getCollapsedHeight()}px`;
  };

  const updateControl = (expanded) => {
    toggle.setAttribute("aria-expanded", String(expanded));
    toggle.querySelector("span:first-child").textContent = expanded
      ? "Show fewer reviews"
      : "Show more reviews";
  };

  setCollapsedHeight();
  window.addEventListener("resize", setCollapsedHeight, { passive: true });

  toggle.addEventListener("click", () => {
    const isExpanded = toggle.getAttribute("aria-expanded") === "true";

    if (!isExpanded) {
      wall.style.overflow = "hidden";
      wall.style.maxHeight = `${wall.getBoundingClientRect().height}px`;
      wall.classList.remove("is-collapsed");
      wall.classList.add("is-expanded");
      updateControl(true);

      window.requestAnimationFrame(() => {
        wall.style.maxHeight = `${wall.scrollHeight}px`;
      });
      return;
    }

    wall.style.overflow = "hidden";
    wall.style.maxHeight = `${wall.getBoundingClientRect().height}px`;
    wall.classList.remove("is-expanded");
    wall.classList.add("is-collapsed");
    updateControl(false);

    window.requestAnimationFrame(() => {
      wall.style.maxHeight = `${getCollapsedHeight()}px`;
    });
  });

  wall.addEventListener("transitionend", (event) => {
    if (event.propertyName !== "max-height") return;

    if (wall.classList.contains("is-expanded")) {
      wall.style.maxHeight = "none";
      wall.style.overflow = "visible";
    } else {
      wall.style.maxHeight = `${getCollapsedHeight()}px`;
      wall.style.overflow = "hidden";
    }
  });
})();

(() => {
  const list = document.querySelector("[data-faq-list]");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  if (!list) return;

  // Keep market-specific copy separate from the rendering and accordion behavior.
  const faqItems = [
    {
      id: "design-process",
      question: "How does the custom design process work?",
      answer: "Tell us what you ride and give us an idea of what you want. Send references, an AI concept, colours, logos or just describe the look. Our designers build the first proof from there, usually within 1–5 business days.",
    },
    {
      id: "design-changes",
      question: "Can I make changes to my design?",
      answer: "Absolutely. We’ll keep refining it with you until it feels right. Nothing goes to print until you’re happy with the design.",
    },
    {
      id: "design-direction",
      question: "What if I don’t know exactly what I want?",
      answer: "That’s completely fine. A screenshot, another bike you like, a colour scheme, a theme or even a rough description is enough to get started. You don’t need to show up with a finished design.",
    },
    {
      id: "bike-fitment",
      question: "Will you design for my exact bike and plastics?",
      answer: "Yes. Stock plastics, aftermarket plastics, restyle kits and older bikes are all part of what we do. Tell us exactly what’s on your bike and we’ll design around that setup. If you can’t find yours in the form, get in touch and we’ll confirm it with you.",
    },
    {
      id: "kit-inclusions",
      question: "What’s included in my graphics kit?",
      answer: "The standard kit covers the main graphics areas of your bike, including the fenders, number plate or headlight, tank and shrouds, side plates, fork guards and swingarm where applicable. If you choose Graphics + Extras, you can add things like fork wraps, mini plates, jersey prints, hub stickers and more.",
    },
    {
      id: "ultracurve",
      question: "What is UltraCurve?",
      answer: "UltraCurve is a 6.0 mil graphics material developed specifically for motocross and powersports. It uses aggressive adhesive for low-surface-energy plastics, while FLO Technology helps air escape during installation for a cleaner result.",
    },
    {
      id: "production-time",
      question: "How long does design and production take?",
      answer: "Your first proof is usually ready within 1–5 business days. Once you approve it, standard production is generally 5–7 business days. Rush production is also available, and some extras can add a little more time.",
    },
    ...(document.querySelector('.omega-mx[data-market="US"]')
      ? [{
          id: "us-shipping",
          question: "How long does shipping to the U.S. take? Will I pay import fees?",
          answer: "U.S. orders ship express, with delivery targeted at around 7 days. Tracking is included, and there are no extra tariffs or import fees added to your order.",
        }]
      : [{
          id: "shipping",
          question: "How long does shipping take?",
          answer: "All kits ship with tracking. Australia and New Zealand: standard or express at checkout. International: express worldwide, with DHL options for some regions. Delivery times depend on the courier and customs.",
        }]),
  ];

  const desktopOpen = window.matchMedia("(min-width: 48.0625rem)").matches;

  const setItemOpen = (trigger, panel, isOpen) => {
    trigger.setAttribute("aria-expanded", String(isOpen));

    if (isOpen) {
      panel.hidden = false;
      window.requestAnimationFrame(() => panel.classList.add("is-open"));
      return;
    }

    panel.classList.remove("is-open");
    if (reducedMotionQuery.matches) {
      panel.hidden = true;
      return;
    }

    const hideIfClosed = () => {
      if (trigger.getAttribute("aria-expanded") === "false") panel.hidden = true;
    };
    const finishCollapse = (event) => {
      if (event.target !== panel || event.propertyName !== "grid-template-rows") return;
      panel.removeEventListener("transitionend", finishCollapse);
      hideIfClosed();
    };

    panel.addEventListener("transitionend", finishCollapse);
    window.setTimeout(() => {
      panel.removeEventListener("transitionend", finishCollapse);
      hideIfClosed();
    }, 340);
  };

  faqItems.forEach((faq, index) => {
    const isOpenInitially = desktopOpen && index === 0;
    const item = document.createElement("div");
    const heading = document.createElement("h3");
    const trigger = document.createElement("button");
    const question = document.createElement("span");
    const control = document.createElement("span");
    const panel = document.createElement("div");
    const answerInner = document.createElement("div");
    const answer = document.createElement("p");
    const triggerId = `omega-faq-question-${faq.id}`;
    const panelId = `omega-faq-answer-${faq.id}`;

    item.className = "omega-faq__item";
    item.setAttribute("role", "listitem");
    heading.className = "omega-faq__heading";
    trigger.className = "omega-faq__trigger";
    trigger.id = triggerId;
    trigger.type = "button";
    trigger.setAttribute("aria-expanded", String(isOpenInitially));
    trigger.setAttribute("aria-controls", panelId);
    question.className = "omega-faq__question";
    question.textContent = faq.question;
    control.className = "omega-faq__control";
    control.setAttribute("aria-hidden", "true");
    panel.className = `omega-faq__answer${isOpenInitially ? " is-open" : ""}`;
    panel.id = panelId;
    panel.setAttribute("role", "region");
    panel.setAttribute("aria-labelledby", triggerId);
    panel.hidden = !isOpenInitially;
    answerInner.className = "omega-faq__answer-inner";
    answer.textContent = faq.answer;

    trigger.append(question, control);
    answerInner.append(answer);
    panel.append(answerInner);
    heading.append(trigger);
    item.append(heading, panel);
    list.append(item);

    trigger.addEventListener("click", () => {
      if (trigger.getAttribute("aria-expanded") === "true") {
        setItemOpen(trigger, panel, false);
        return;
      }

      const openTrigger = list.querySelector('.omega-faq__trigger[aria-expanded="true"]');
      if (openTrigger) {
        const openPanel = document.getElementById(openTrigger.getAttribute("aria-controls"));
        if (openPanel) setItemOpen(openTrigger, openPanel, false);
      }

      setItemOpen(trigger, panel, true);
    });
  });
})();

/* ---- LPO bridge: makes the live LPO form sit inside the design ---- */
(() => {
  const form = document.getElementById("OmegaMxProductForm");
  if (!form) return;
  const summaryTarget = document.querySelector("[data-omx-summary]");
  const submitWrap = form.querySelector("[data-omx-submit-wrap]");
  const fallback = form.querySelector("[data-omx-fallback]");
  const hint = form.querySelector("[data-omx-upload-hint]");

  // The LPO UX script paints the card and tab bar with inline !important
  // styles once at start-up. Strip them here so the page styles apply.
  const INLINE = {
    ".cl-po--options": ["background", "border", "border-radius", "padding", "box-shadow"],
    ".omega-tabs": ["background"],
  };
  const stripInline = () => {
    Object.entries(INLINE).forEach(([sel, props]) => {
      form.querySelectorAll(sel).forEach((el) => {
        props.forEach((p) => { if (el.style.getPropertyValue(p)) el.style.removeProperty(p); });
      });
    });
  };

  // Mirror LPO's price summary into the design's "Your Kit" card (read only).
  let lastSummary = "";
  const mirrorSummary = () => {
    const src = form.querySelector(".cl-po--price-summary");
    if (!src || !summaryTarget) return;
    const html = src.innerHTML;
    if (html !== lastSummary) { lastSummary = html; summaryTarget.innerHTML = html; }
  };

  // LPO marks the mockup/reference uploads required but never enforces them in the
  // browser, so a customer could reach the last step and press Order with no file.
  const REQUIRED_UPLOAD = { "AI mockup": "AI mockup", "Reference": "Reference image" }; // design source -> option
  const SECOND_UPLOAD = { "AI mockup 2": "AI mockup", "Reference image 2": "Reference" }; // option -> design source
  const designSource = () => {
    const r = form.querySelector('input[name="properties[Design source]"]:checked');
    return r ? r.value : "";
  };
  const uploadHasFile = (opt) => {
    const file = opt.querySelector('input[type="file"]');
    if (file && file.files && file.files.length) return true;
    if ([...opt.querySelectorAll('input[type="hidden"]')].some((i) => i.value)) return true;
    return !!opt.querySelector('[class*="upload-file"], [class*="file-name"], [class*="upload-files"] > *');
  };
  const missingUploadOption = () => {
    const name = REQUIRED_UPLOAD[designSource()];
    if (!name) return null;
    const opt = form.querySelector('.cl-po--option[data-option="' + name + '"]');
    if (!opt) return null;
    return uploadHasFile(opt) ? null : opt;
  };
  // LPO shows the "another angle" upload for every path; only its own path should see it.
  const syncSecondUploads = () => {
    const src = designSource();
    Object.entries(SECOND_UPLOAD).forEach(([opt, path]) => {
      form.querySelectorAll('.cl-po--option[data-option="' + opt + '"]')
        .forEach((el) => el.classList.toggle("omx-hidden", src !== path));
    });
  };

  // Show the order button only once LPO has rendered, and only on the last step.
  const syncSubmit = () => {
    if (!submitWrap) return;
    const rendered = !!form.querySelector(".cl-po--wrapper");
    const onLast = !!form.querySelector(".omega-nav.omega-nav-last");
    const paged = !!form.querySelector(".omega-paged");
    submitWrap.hidden = !rendered || (paged && !onLast);
    if (hint) hint.hidden = submitWrap.hidden || !missingUploadOption();
  };
  const syncA11y = () => {
    const tabs = form.querySelector(".omega-tabs");
    if (!tabs) return;
    if (!tabs.getAttribute("aria-label")) tabs.setAttribute("aria-label", "Order steps");
    tabs.querySelectorAll(".omega-tab").forEach((t) => {
      if (t.classList.contains("omega-active")) t.setAttribute("aria-current", "step");
      else t.removeAttribute("aria-current");
      const n = t.querySelector(".omega-tab-num");
      if (n) n.setAttribute("aria-hidden", "true");
    });
  };

  // Validation feedback: route to the step that holds the problem and say what is missing.
  const STEP_OF = (name) => {
    if (["Step1", "Brand", "Brands", "Bike Year", "Bike not found", "Bike Details"].includes(name)) return 1;
    if (name.indexOf("Step 2") === 0) return 2;
    if (name.indexOf("Step 3") === 0) return 3;
    return 4;
  };
  const topOption = (el) => {
    let n = el;
    while (n && n !== form) {
      if (n.classList && n.classList.contains("cl-po--option") && n.parentElement && n.parentElement.classList.contains("cl-po--options")) return n;
      n = n.parentElement;
    }
    return null;
  };
  const showError = (opt, control, text) => {
    opt.classList.add("omx-invalid");
    let msg = opt.querySelector(".omx-field-error");
    if (!msg) {
      msg = document.createElement("p");
      msg.className = "omx-field-error";
      msg.setAttribute("role", "alert");
      (control && control.parentElement ? control.parentElement : opt).appendChild(msg);
    }
    msg.textContent = text;
  };
  const clearError = (opt) => {
    opt.classList.remove("omx-invalid");
    const msg = opt.querySelector(".omx-field-error");
    if (msg) msg.remove();
  };
  const routeTo = (el, text) => {
    const top = topOption(el);
    if (!top) return;
    const tab = form.querySelector('.omega-tab[data-omega-tab="' + STEP_OF(top.getAttribute("data-option") || "") + '"]');
    if (tab && !tab.classList.contains("omega-active") && !tab.disabled) tab.click();
    const opt = el.closest(".cl-po--option") || top;
    showError(opt, el, text);
    setTimeout(() => { try { opt.scrollIntoView({ behavior: "auto", block: "center" }); } catch (e) {} }, 450);
  };
  form.addEventListener("invalid", (e) => {
    const opt = e.target.closest && e.target.closest(".cl-po--option");
    if (opt) showError(opt, e.target, "Please complete this field to continue.");
  }, true);
  ["input", "change"].forEach((evt) => form.addEventListener(evt, (e) => {
    const opt = e.target.closest && e.target.closest(".cl-po--option.omx-invalid");
    if (opt) clearError(opt);
  }));
  form.addEventListener("click", (e) => {
    if (!e.target.closest || !e.target.closest(".omega-mx-submit")) return;
    // LPO blocks the submit and marks the field, but fires no event we can catch.
    setTimeout(() => {
      const bad = form.querySelector(".cl-po--invalid");
      if (bad) routeTo(bad, "Please add your file to continue.");
    }, 400);
  });
  if (hint) hint.addEventListener("click", (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    e.preventDefault();
    const opt = missingUploadOption();
    if (opt) routeTo(opt.querySelector('input[type="file"]') || opt, "Please add your file to continue.");
  });

  // Add to cart in the background and open the theme's cart drawer (Impulse listens for
  // ajaxProduct:added), instead of the full-page POST that lands on /cart. LPO validates
  // first: when it blocks the submit, defaultPrevented is already true and we do nothing.
  form.addEventListener("submit", (e) => {
    if (e.defaultPrevented) return;
    const settings = (window.theme && window.theme.settings) || {};
    if (settings.cartType !== "drawer") return; // keep the native behaviour on other cart types
    e.preventDefault();
    const btn = form.querySelector(".omega-mx-submit");
    const old = form.querySelector(".omx-cart-error");
    if (old) old.remove();
    if (btn) { btn.disabled = true; btn.setAttribute("aria-busy", "true"); }
    const url = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || "/cart/add.js";
    fetch(url, { method: "POST", body: new FormData(form), headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" } })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok || j.status === 422) throw new Error(j.description || j.message || "Could not add to cart");
        document.dispatchEvent(new CustomEvent("ajaxProduct:added", { detail: { product: j, addToCartBtn: btn }, bubbles: true }));
      })
      .catch((err) => {
        const p = document.createElement("p");
        p.className = "omx-field-error omx-cart-error";
        p.setAttribute("role", "alert");
        p.textContent = err.message + ". Please try again or contact us.";
        (submitWrap || form).appendChild(p);
      })
      .finally(() => { if (btn) { btn.disabled = false; btn.removeAttribute("aria-busy"); } });
  });

  const sync = () => { stripInline(); mirrorSummary(); syncSecondUploads(); syncSubmit(); syncA11y(); };
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; sync(); });
  }).observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ["style", "class"], characterData: true });
  ["options:rendered", "options:price", "option:changed"].forEach((evt) => window.addEventListener(evt, () => setTimeout(sync, 80)));
  sync();
  setTimeout(() => { if (fallback && !form.querySelector(".cl-po--wrapper")) fallback.hidden = false; }, 12000);
})();
